import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from "recharts";
import { api, fmtMoney, fmtMoneyCents, fmtDate, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Car, FileText, CreditCard, Bell, UserCog, Clock, CheckCircle2, CalendarClock, Wallet, Loader2,
} from "lucide-react";

const STATUS_COLOR = {
  Draft: "bg-slate-500/15 text-slate-700 border-slate-500/30",
  Submitted: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  "Under Review": "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  "Additional Information Required": "bg-amber-500/15 text-amber-400 border-amber-500/30",
  Approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  "Conditionally Approved": "bg-teal-500/15 text-teal-400 border-teal-500/30",
  Declined: "bg-red-500/15 text-red-400 border-red-500/30",
  Cancelled: "bg-slate-500/15 text-slate-600 border-slate-500/30",
  Completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};
const PAY_COLOR = {
  Paid: "bg-emerald-500/15 text-emerald-400", Upcoming: "bg-slate-500/15 text-slate-700",
  Due: "bg-amber-500/15 text-amber-400", Late: "bg-red-500/15 text-red-400",
  Failed: "bg-red-500/15 text-red-400", Pending: "bg-blue-500/15 text-blue-400",
};

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = params.get("tab") || "overview";
  const [data, setData] = useState(null);
  const [apps, setApps] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [profile, setProfile] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  const loadAll = () => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/applications/mine").then((r) => setApps(r.data)).catch(() => {});
    api.get("/payments/mine").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (user) setProfile(user); }, [user]);

  const setTab = (t) => setParams(t === "overview" ? {} : { tab: t });

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); loadAll(); };
  const markAllRead = async () => { await api.post("/notifications/read-all"); loadAll(); };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", {
        name: profile.name, phone: profile.phone, date_of_birth: profile.date_of_birth,
        address: profile.address, city: profile.city, state: profile.state, zip_code: profile.zip_code,
      });
      const me = await api.get("/auth/me"); setUser(me.data);
      toast.success("Profile updated");
    } finally { setSavingProfile(false); }
  };

  const c = data?.contract;
  const unread = notifs.filter((n) => !n.read).length;

  return (
    <Layout>
      <div className="container-x py-10">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Welcome back, {user?.name?.split(" ")[0] || "Driver"}</h1>
            <p className="mt-1 text-slate-600">Manage your applications, financing, and payments.</p>
          </div>
          <Button asChild variant="outline" className="border-slate-200 bg-transparent text-slate-900 hover:bg-slate-100 hover:text-slate-900"><Link to="/inventory">Browse Inventory</Link></Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap bg-[#FFFFFF]">
            {[["overview", "Overview", Car], ["applications", "Applications", FileText], ["payments", "Payments", CreditCard], ["notifications", "Notifications", Bell], ["profile", "Profile", UserCog]].map(([v, l, Icon]) => (
              <TabsTrigger key={v} value={v} data-testid={`dash-tab-${v}`} className="gap-1.5 data-[state=active]:bg-gold data-[state=active]:text-[#EEF3FC]">
                <Icon className="h-4 w-4" /> {l}
                {v === "notifications" && unread > 0 && <span className="ml-1 rounded-full bg-gold px-1.5 text-[10px] font-bold text-[#EEF3FC] data-[state=active]:bg-[#EEF3FC] data-[state=active]:text-gold">{unread}</span>}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-6">
            {c ? (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="customer-dashboard-financing">
                <div className="card-surface overflow-hidden lg:col-span-2">
                  <div className="flex flex-col sm:flex-row">
                    <img src={imgUrl(c.vehicle_summary.image)} alt="" className="h-48 w-full object-cover sm:h-auto sm:w-56" />
                    <div className="flex-1 p-6">
                      <Badge className="bg-emerald-500/15 text-emerald-400">Active Financing</Badge>
                      <h3 className="mt-3 font-display text-2xl font-bold text-slate-900">{c.vehicle_summary.make} {c.vehicle_summary.model}</h3>
                      <p className="text-slate-600">{c.vehicle_summary.year} {c.vehicle_summary.trim}</p>
                      <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
                        {[["Monthly", fmtMoney(c.monthly_payment)], ["Down Paid", fmtMoney(c.down_payment)], ["Financed", fmtMoney(c.principal)], ["Remaining", fmtMoney(c.remaining_balance)], ["Term", `${c.term} mo`], ["APR", `${c.apr}%`]].map(([l, v]) => (
                          <div key={l}><div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div><div className="font-display font-bold text-slate-900">{v}</div></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress gauge */}
                <div className="card-surface p-6" data-testid="customer-dashboard-payment-progress">
                  <h4 className="font-display font-bold text-slate-900">Payment Progress</h4>
                  <div className="relative mx-auto mt-2 h-40 w-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadialBarChart innerRadius="72%" outerRadius="100%" data={[{ value: c.progress_pct, fill: "#2E8BFF" }]} startAngle={90} endAngle={-270}>
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar background={{ fill: "#DCE7FA" }} dataKey="value" cornerRadius={20} />
                      </RadialBarChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                      <span className="font-display text-3xl font-extrabold text-gold">{c.progress_pct}%</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500">Paid</span>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-between text-xs">
                    <span className="text-emerald-400">Paid {fmtMoney(c.paid_amount)}</span>
                    <span className="text-slate-600">Remaining {fmtMoney(c.remaining_balance)}</span>
                  </div>
                  <div className="mt-4 rounded-lg border border-slate-200 bg-[#EEF3FC] p-3">
                    <div className="flex items-center gap-2 text-sm text-slate-900"><CalendarClock className="h-4 w-4 text-gold" /> Next Payment</div>
                    {c.next_payment ? (
                      <p className="mt-1 text-sm text-slate-600">{fmtMoney(c.next_payment.amount)} due {fmtDate(c.next_payment.due_date)}</p>
                    ) : <p className="mt-1 text-sm text-slate-600">No upcoming payment</p>}
                  </div>
                  <div className="mt-2 text-center text-xs text-slate-500">{c.months_paid} of {c.term} months paid · {c.months_remaining} remaining</div>
                </div>
              </div>
            ) : (
              <div className="card-surface p-12 text-center">
                <Wallet className="mx-auto h-12 w-12 text-slate-600" />
                <h3 className="mt-4 font-display text-xl font-bold text-slate-900">No Active Financing</h3>
                <p className="mt-2 text-slate-600">Once an application is approved, your financing details and payment progress appear here.</p>
                <Button asChild className="btn-gold mt-6 rounded-full px-8"><Link to="/inventory">Explore Inventory</Link></Button>
              </div>
            )}
          </TabsContent>

          {/* APPLICATIONS */}
          <TabsContent value="applications" className="mt-6">
            {apps.length === 0 ? (
              <div className="card-surface p-12 text-center text-slate-600">No applications yet. <Link to="/inventory" className="text-gold">Start one</Link>.</div>
            ) : (
              <div className="space-y-4">
                {apps.map((a) => (
                  <div key={a.id} className="card-surface p-6" data-testid={`app-row-${a.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex gap-4">
                        <img src={imgUrl(a.vehicle_summary.image)} alt="" className="h-20 w-28 rounded-lg object-cover" />
                        <div>
                          <h3 className="font-display text-lg font-bold text-slate-900">{a.vehicle_summary.make} {a.vehicle_summary.model}</h3>
                          <p className="text-sm text-slate-600">{a.vehicle_summary.year} · Est. {fmtMoney(a.financing.monthly_payment)}/mo</p>
                          <p className="mt-1 text-xs text-slate-500">Started {fmtDate(a.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={`border ${STATUS_COLOR[a.status]}`}>{a.status}</Badge>
                        {(a.status === "Draft" || a.status === "Additional Information Required") && (
                          <Button size="sm" onClick={() => navigate(`/apply/${a.id}`)} data-testid={`app-continue-${a.id}`} className="btn-gold rounded-full">{a.status === "Draft" ? "Continue" : "Provide Info"}</Button>
                        )}
                      </div>
                    </div>
                    {/* Timeline */}
                    <div className="mt-5 border-t border-slate-200 pt-4">
                      <div className="flex flex-wrap gap-x-6 gap-y-2">
                        {a.timeline?.map((t, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <CheckCircle2 className="h-3.5 w-3.5 text-gold" />
                            <span className="text-slate-700">{t.status}</span>
                            <span className="text-slate-600">{fmtDate(t.at)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PAYMENTS */}
          <TabsContent value="payments" className="mt-6">
            {payments.length === 0 ? (
              <div className="card-surface p-12 text-center text-slate-600">No payment schedule yet.</div>
            ) : (
              <div className="card-surface overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="p-4">#</th><th className="p-4">Due Date</th><th className="p-4">Amount</th><th className="p-4">Status</th><th className="p-4">Paid Date</th><th className="p-4">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} className="border-b border-slate-200/60 text-sm" data-testid={`payment-row-${p.number}`}>
                        <td className="p-4 text-slate-600">{p.number}</td>
                        <td className="p-4 text-slate-900">{fmtDate(p.due_date)}</td>
                        <td className="p-4 font-mono text-slate-900">{fmtMoneyCents(p.amount)}</td>
                        <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${PAY_COLOR[p.status]}`}>{p.status}</span></td>
                        <td className="p-4 text-slate-600">{fmtDate(p.paid_date)}</td>
                        <td className="p-4 font-mono text-xs text-slate-500">{p.reference || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* NOTIFICATIONS */}
          <TabsContent value="notifications" className="mt-6">
            <div className="mb-4 flex justify-end">{unread > 0 && <Button size="sm" variant="outline" onClick={markAllRead} className="border-slate-200 bg-transparent text-slate-700 hover:bg-slate-100 hover:text-slate-900">Mark all read</Button>}</div>
            {notifs.length === 0 ? (
              <div className="card-surface p-12 text-center text-slate-600">No notifications.</div>
            ) : (
              <div className="space-y-3">
                {notifs.map((n) => (
                  <div key={n.id} onClick={() => !n.read && markRead(n.id)} className={`card-surface flex cursor-pointer items-start gap-4 p-5 ${!n.read ? "border-gold/30" : ""}`} data-testid={`notif-${n.id}`}>
                    <div className={`mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${n.read ? "bg-slate-100 text-slate-500" : "bg-gold/15 text-gold"}`}><Bell className="h-4 w-4" /></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between"><h4 className="font-semibold text-slate-900">{n.title}</h4>{!n.read && <span className="h-2 w-2 rounded-full bg-gold" />}</div>
                      <p className="text-sm text-slate-600">{n.message}</p>
                      <p className="mt-1 text-xs text-slate-600">{fmtDate(n.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PROFILE */}
          <TabsContent value="profile" className="mt-6">
            <div className="card-surface max-w-2xl p-6 sm:p-8">
              <h3 className="mb-6 font-display text-xl font-bold text-slate-900">Profile Settings</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[["Full Name", "name"], ["Phone", "phone"], ["Date of Birth", "date_of_birth"], ["ZIP Code", "zip_code"], ["Address", "address"], ["City", "city"], ["State", "state"]].map(([l, k]) => (
                  <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
                    <label className="mb-1.5 block text-sm text-slate-700">{l}</label>
                    <Input data-testid={`profile-${k}`} type={k === "date_of_birth" ? "date" : "text"} value={profile[k] || ""} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} className="border-slate-200 bg-[#EEF3FC] text-slate-900" />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm text-slate-700">Email</label>
                  <Input disabled value={profile.email || ""} className="border-slate-200 bg-[#EEF3FC] text-slate-500" />
                </div>
              </div>
              <Button onClick={saveProfile} disabled={savingProfile} data-testid="profile-save-btn" className="btn-gold mt-6 rounded-full px-8">{savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}</Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
